import { CSSObject, Global } from '@emotion/react';
import React, { CSSProperties, useContext, useMemo, useState } from 'react';
import { ContentBreakpointsContext } from '@hooks/useBreakpoints';
import { Z_INDEX } from '@components/shared/zIndex';
import useResizeObserverRef from '@hooks/useResizeObserverRef';
import { TOAST_PORTAL_CONTAINER_ID } from '@components/engagement/constants';
import { contentPanelEndId, contentPanelStartId, PanelPortalContext, PanelPortalProvider } from './AppPanelPortal';

interface Props {
  header?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}

const useStyles = ({
  headerHeight,
  titleHeight,
  panelPosition,
  panelZIndex
}: {
  headerHeight: number;
  titleHeight: number;
  panelPosition: 'start' | 'end' | 'bottom';
  panelZIndex: CSSProperties['zIndex'];
}): Record<string, CSSObject> => {
  let containerGridStyles: CSSObject;
  let panelHeight: CSSObject['height'] = null;
  let panelTop: CSSObject['top'] = null;

  const hasTitle = titleHeight > 0;

  if (panelPosition === 'start') {
    containerGridStyles = {
      gridTemplateAreas: `
          "header header"
          ${hasTitle ? '"pageTitle pageTitle"' : ''}
          "panel main"
          "panel footer"
        `,
      gridTemplateColumns: 'auto minmax(0,1fr)',
      gridTemplateRows: `auto ${hasTitle ? 'auto' : ''} 1fr auto`
    };
    panelHeight = `calc(100vh - ${headerHeight}px)`;
    panelTop = headerHeight;
  } else if (panelPosition === 'end') {
    containerGridStyles = {
      gridTemplateAreas: `
            "header header"
            ${hasTitle ? '"pageTitle pageTitle"' : ''}
            "main panel"
            "footer panel"
          `,
      gridTemplateColumns: 'minmax(0,1fr) auto',
      gridTemplateRows: `auto ${hasTitle ? 'auto' : ''} 1fr auto`
    };
    panelHeight = `calc(100vh - ${headerHeight}px)`;
    panelTop = headerHeight;
  } else if (panelPosition === 'bottom') {
    containerGridStyles = {
      display: 'flex',
      flexDirection: 'column'
    };
  }

  return {
    container: {
      minHeight: '100vh',
      display: 'grid',
      overflowX: 'clip',
      ...containerGridStyles
    },
    header: {
      gridArea: 'header',
      position: 'sticky',
      top: 0,
      left: 0,
      right: 0,
      zIndex: Z_INDEX.APP_LAYOUT_HEADER
    },
    toast: {
      zIndex: Z_INDEX.ENGAGEMENT_TOASTS_ACTIVE
    },
    main: {
      gridArea: 'main',
      // Prevents main content from being scrolled to under the nav bar after using skip nav link
      paddingTop: headerHeight,
      marginTop: -headerHeight
    },
    panel: {
      /*
        - Panel should "stick" to bottom of page when panelPosition is bottom
        - Panel should "stick" under the header otherwise
      */
      height: panelHeight,
      position: 'sticky',
      top: panelTop,
      right: 0,
      bottom: 0
    },
    panelContainer: {
      gridArea: 'panel',
      zIndex: panelZIndex
    },
    footer: {
      gridArea: 'footer'
    }
  };
};

function Layout({ header, children, footer }: Props): JSX.Element {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [titleHeight, setTitleHeight] = useState(0);
  const headerRef = useResizeObserverRef<HTMLElement>((entry: ResizeObserverEntry) => {
    const height = entry.target.clientHeight;
    if (height !== headerHeight) {
      setHeaderHeight(height);
    }
  });

  const titleRef = useResizeObserverRef<HTMLDivElement>((entry: ResizeObserverEntry) => {
    const height = entry.target.clientHeight;
    if (height !== titleHeight) {
      setTitleHeight(height);
    }
  });

  const { hasPanel, position: panelPosition, zIndex: panelZIndex } = useContext(PanelPortalContext);
  const styles = useStyles({ headerHeight, titleHeight, panelPosition, panelZIndex });
  const hasPanelStart = hasPanel && panelPosition === 'start';
  const hasPanelBottomOrEnd = hasPanel && (panelPosition === 'end' || panelPosition === 'bottom');

  const contentBreakpointsValue = useMemo(() => ({ contentTop: headerHeight }), [headerHeight]);

  return (
    <ContentBreakpointsContext.Provider value={contentBreakpointsValue}>
      <div css={styles.container}>
        <Global styles={{ html: { scrollPaddingTop: headerHeight } }} />
        {header && (
          <nav ref={headerRef} css={styles.header}>
            {header}
          </nav>
        )}
        {/* This div + id is necessary as a target element for the toast portal created in PageHeader */}
        <div id={TOAST_PORTAL_CONTAINER_ID} css={styles.toast} />

        {/* pageTitle is a sticky element that is used to anchor the title of the page under the nav bar */}
        <header css={{ width: '100%', gridArea: 'pageTitle', left: 0, right: 0 }} aria-hidden={titleHeight === 0}>
          <div id="pageTitle" ref={titleRef} css={{ position: 'sticky', top: headerHeight }} />
        </header>

        {/* aside must always be rendered so it can be selected by createPortal,
        but we hide it from view / screen readers when no panel is rendered */}
        <div css={hasPanelStart ? styles.panelContainer : null}>
          <aside
            id={contentPanelStartId}
            css={hasPanelStart ? styles.panel : null}
            hidden={!hasPanelStart}
            aria-hidden={!hasPanelStart}
          />
        </div>

        <main id="content" css={styles.main}>
          {children}
        </main>

        <div
          css={[hasPanelBottomOrEnd ? styles.panelContainer : null, panelPosition === 'bottom' ? styles.panel : null]}
        >
          <aside
            id={contentPanelEndId}
            css={hasPanelBottomOrEnd && panelPosition !== 'bottom' ? styles.panel : null}
            hidden={!hasPanelBottomOrEnd}
            aria-hidden={!hasPanelBottomOrEnd}
          />
        </div>

        <footer css={styles.footer}>{footer}</footer>
      </div>
    </ContentBreakpointsContext.Provider>
  );
}

export default function AppLayout(props: Props): JSX.Element {
  return (
    <PanelPortalProvider>
      <Layout {...props} />
    </PanelPortalProvider>
  );
}
